import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, Shield, ShieldCheck, X, Mail, MessageSquare, FileWarning, Plus, Phone } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner, StatusPill } from "@/components/tac";
import EscalationChain from "@/components/EscalationChain";

const ROLE_COLOR = { owner: "text-amber-400", dispatcher: "text-cyan-400", technician: "text-emerald-400", subcontractor: "text-slate-400" };

export default function Team() {
  const qc = useQueryClient();
  const { user, refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfa, setMfa] = useState({ secret: "", uri: "", code: "" });
  const [inv, setInv] = useState({ name: "", email: "", role: "technician" });
  const [coiDate, setCoiDate] = useState("");
  const [phoneDraft, setPhoneDraft] = useState({});

  const savePhone = async (m) => {
    const v = (phoneDraft[m.id] ?? "").trim();
    if (!v) { toast.error("Enter a phone number"); return; }
    try { await api.put(`/auth/team/${m.id}/phone`, { phone: v }); toast.success("Phone saved for dispatch"); setPhoneDraft((d) => ({ ...d, [m.id]: undefined })); qc.invalidateQueries({ queryKey: ["team"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const { data: team, isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const { data: invites } = useQuery({ queryKey: ["invites"], queryFn: async () => (await api.get("/auth/invitations")).data });
  const { data: compliance } = useQuery({ queryKey: ["compliance"], queryFn: async () => (await api.get("/compliance")).data });

  const setPrefs = async (m, email, sms) => {
    try { await api.put(`/auth/team/${m.id}/alert-prefs`, { alert_email: email, alert_sms: sms }); toast.success("Alert prefs updated"); qc.invalidateQueries({ queryKey: ["team"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addCoi = async () => {
    const sub = (team || []).find((t) => t.role === "subcontractor");
    try { await api.post("/compliance", { type: "COI", holder: sub?.name || "Subcontractor", subcontractor_id: sub?.id, expires_at: coiDate ? `${coiDate}T00:00:00Z` : null }); toast.success("COI added"); setCoiDate(""); qc.invalidateQueries({ queryKey: ["compliance"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setCoiStatus = async (cid, status) => {
    try { await api.post(`/compliance/${cid}/status`, { status }); toast.success(`COI ${status}`); qc.invalidateQueries({ queryKey: ["compliance"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const sendInvite = async () => {
    try { const { data } = await api.post("/auth/invite", inv); toast.success(`Invite created (dev token: ${data.dev_token.slice(0, 10)}…)`); setOpen(false); setInv({ name: "", email: "", role: "technician" }); qc.invalidateQueries({ queryKey: ["invites"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const enrollMfa = async () => {
    try { const { data } = await api.post("/auth/mfa/enroll"); setMfa({ secret: data.secret, uri: data.otpauth_uri, code: "" }); setMfaOpen(true); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const verifyMfa = async () => {
    try { await api.post("/auth/mfa/verify", { code: mfa.code }); toast.success("MFA enabled"); setMfaOpen(false); refreshMe(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-center justify-between">
        <div><Eyebrow>Access control</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Team & roles</h1></div>
        <div className="flex gap-2">
          {["owner", "dispatcher"].includes(user?.role) && (
            <button data-testid="enroll-mfa-btn" onClick={enrollMfa} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm text-white hover:border-emerald-500">
              {user?.mfa_enabled ? <ShieldCheck className="h-4 w-4 text-emerald-400" /> : <Shield className="h-4 w-4" />} {user?.mfa_enabled ? "MFA on" : "Enable MFA"}
            </button>
          )}
          <button data-testid="invite-btn" onClick={() => setOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><UserPlus className="h-4 w-4" /> Invite</button>
        </div>
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 card-tac divide-y divide-slate-800">
          {team.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-5 py-4" data-testid={`team-member-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-800 font-display font-bold text-white">{m.name?.[0]}</div>
                <div><div className="text-sm text-white">{m.name}</div><div className="font-mono-tac text-xs text-slate-500">{m.email}</div>
                  {m.phone ? (
                    <div className="mt-0.5 flex items-center gap-1 font-mono-tac text-[10px] text-slate-500"><Phone className="h-3 w-3" />{m.phone}</div>
                  ) : (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`no-phone-${m.id}`}>
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300"><Phone className="h-3 w-3" /> Phone required for dispatch</span>
                      <input data-testid={`phone-input-${m.id}`} value={phoneDraft[m.id] ?? ""} onChange={(e) => setPhoneDraft((d) => ({ ...d, [m.id]: e.target.value }))} placeholder="+1…" className="w-28 rounded border border-slate-700 bg-[#0d1420] px-2 py-0.5 text-[11px] text-white focus:border-amber-500 focus:outline-none" />
                      <button data-testid={`phone-save-${m.id}`} onClick={() => savePhone(m)} className="rounded-lg border border-cyan-600/40 px-3 py-1.5 text-xs text-cyan-300 hover:border-cyan-400">Save</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {m.mfa_enabled && <ShieldCheck className="h-4 w-4 text-emerald-400" />}
                {["owner", "dispatcher"].includes(m.role) && (
                  <div className="flex items-center gap-1">
                    <button data-testid={`alert-email-${m.id}`} onClick={() => setPrefs(m, !(m.alert_email ?? true), m.alert_sms ?? false)}
                      title="Email alerts" className={`rounded-md border p-1.5 ${(m.alert_email ?? true) ? "border-amber-500 text-amber-400" : "border-slate-700 text-slate-600"}`}><Mail className="h-3.5 w-3.5" /></button>
                    <button data-testid={`alert-sms-${m.id}`} onClick={() => setPrefs(m, m.alert_email ?? true, !(m.alert_sms ?? false))}
                      title="SMS alerts" className={`rounded-md border p-1.5 ${(m.alert_sms ?? false) ? "border-cyan-500 text-cyan-400" : "border-slate-700 text-slate-600"}`}><MessageSquare className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <span className={`font-mono-tac text-xs uppercase tracking-widest ${ROLE_COLOR[m.role]}`}>{m.role}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {invites?.filter((i) => i.status === "pending").length > 0 && (        <div className="mt-6">
          <h2 className="mb-3 font-mono-tac text-xs uppercase tracking-widest text-slate-500">Pending invitations</h2>
          <div className="space-y-2">
            {invites.filter((i) => i.status === "pending").map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-xl border border-slate-800 px-4 py-3 text-sm" data-testid={`invite-row-${i.id}`}>
                <span className="text-slate-300">{i.name} · {i.email}</span>
                <span className="font-mono-tac text-xs text-amber-400">{i.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-mono-tac text-xs uppercase tracking-widest text-slate-500">COI & license vault</h2>
          <div className="flex items-center gap-2">
            <input data-testid="coi-expiry-input" type="date" value={coiDate} onChange={(e) => setCoiDate(e.target.value)}
              className="rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-1 text-xs text-white focus:border-amber-500 focus:outline-none" />
            <button data-testid="add-coi-btn" onClick={addCoi} className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"><Plus className="h-3 w-3" /> Add COI</button>
          </div>
        </div>
        <div className="card-tac divide-y divide-slate-800">
          {(compliance || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between px-5 py-3" data-testid={`coi-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <FileWarning className={`h-4 w-4 ${c.status === "expired" ? "text-rose-400" : c.status === "expiring" ? "text-amber-400" : "text-emerald-400"}`} />
                <div>
                  <div className="text-sm text-white">{c.holder} · {c.type}</div>
                  <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{c.status}{c.expires_at ? ` · exp ${String(c.expires_at).slice(0, 10)}` : ""}{c.status === "expired" ? " · dispatch locked" : ""}</div>
                </div>
              </div>
              <select data-testid={`coi-status-${c.id}`} value={c.status} onChange={(e) => setCoiStatus(c.id, e.target.value)}
                className="rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-1.5 text-xs text-white focus:border-amber-500 focus:outline-none">
                <option value="active">active</option><option value="expiring">expiring</option><option value="expired">expired</option>
              </select>
            </div>
          ))}
          {!(compliance || []).length && <div className="px-5 py-6 text-center text-sm text-slate-500">No COIs on file.</div>}
        </div>
      </div>

      {user?.role === "owner" && <EscalationChain />}

      {open && (
        <Modal title="Invite team member" onClose={() => setOpen(false)}>
          <input data-testid="invite-name-input" placeholder="Name" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className={inp} />
          <input data-testid="invite-email-input" placeholder="Email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className={inp} />
          <select data-testid="invite-role-select" value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })} className={inp}>
            <option value="dispatcher">Dispatcher</option><option value="technician">Technician</option><option value="subcontractor">Subcontractor</option>
          </select>
          <button data-testid="invite-submit" disabled={!inv.name || !inv.email} onClick={sendInvite} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Send invite</button>
        </Modal>
      )}
      {mfaOpen && (
        <Modal title="Enable MFA (TOTP)" onClose={() => setMfaOpen(false)}>
          <p className="text-sm text-slate-400">Add this secret to your authenticator app, then enter the 6-digit code.</p>
          <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-3 font-mono-tac text-sm text-cyan-400 break-all" data-testid="mfa-secret">{mfa.secret}</div>
          <input data-testid="mfa-code-input" placeholder="6-digit code" value={mfa.code} onChange={(e) => setMfa({ ...mfa, code: e.target.value })} className={inp} />
          <button data-testid="mfa-verify-submit" onClick={verifyMfa} className="btn-amber w-full rounded-xl py-3 text-sm">Verify & enable</button>
        </Modal>
      )}
    </div>
  );
}

const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none";
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card-tac w-full max-w-md space-y-3 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold text-white">{title}</h2><button onClick={onClose} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}
